import React, { useState } from "react";
import moment from "moment-timezone";

import { DatePicker, DatePickerInput, Loading, TableCell, Tooltip } from "carbon-components-react";
import { TaskShape } from "@eave/shared/types/api";
import { dueDateParamsToDisplayString } from "./helpers";

interface InlineDateEditingCellProps {
  task: TaskShape;

  updateFieldInline: Function;
}

const getTooltipText = (task): string => {
  if (task.resolved_due_date === task.operator_override_due_date && !task.computed_due_date) {
    return "operator set, no computed due date";
  }

  const dueDateParamsDisplayString =
    task && task.due_date_params ? dueDateParamsToDisplayString(task.due_date_params) : "";

  if (task.resolved_due_date === task.operator_override_due_date && task.computed_due_date) {
    return `operator override, computed due date is ${task.computed_due_date} (${dueDateParamsDisplayString})`;
  }

  if (task.resolved_due_date === task.computed_due_date) {
    return `computed due date: ${dueDateParamsDisplayString}`;
  }
};

const InlineDateEditingCell: React.FC<InlineDateEditingCellProps> = ({
  task,
  updateFieldInline,
}: InlineDateEditingCellProps) => {
  const operatorOverrideKey = "operator_override_due_date";
  const [taskDate, setTaskDate] = useState(task.resolved_due_date);
  const [invalid, setInvalid] = useState(false);
  const [loading, setLoading] = useState(false);

  return (
    <TableCell key={`${task.id}-resolved-due-date`}>
      <div style={{ display: "flex" }}>
        <Loading active={loading} withOverlay />
        {!loading && (
          <DatePicker datePickerType="simple">
            <DatePickerInput
              labelText=""
              invalid={invalid}
              id={`date-picker-input-${task.id}`}
              value={taskDate}
              onChange={e => setTaskDate(e.target.value)}
              onBlur={e => {
                // These two MomentJS arguments will force the string to meet the specified format
                const CIVIL_DATE_FORMAT = "YYYY-MM-DD";
                const REQUIRE_CIVIL_DATE_FORMAT = true;

                const dateOne = moment(
                  task.resolved_due_date,
                  CIVIL_DATE_FORMAT,
                  REQUIRE_CIVIL_DATE_FORMAT,
                );

                const dateTwo = moment(
                  e.target.value,
                  CIVIL_DATE_FORMAT,
                  REQUIRE_CIVIL_DATE_FORMAT,
                );

                if (!dateOne.isValid() || !dateTwo.isValid()) {
                  setInvalid(true);
                  return;
                }

                if (dateOne.isSame(dateTwo, "day")) {
                  return;
                }

                // Inputs are valid
                setInvalid(false);
                setLoading(true);

                updateFieldInline(task, operatorOverrideKey, e.target.value, () =>
                  setLoading(false),
                );
              }}
            />
          </DatePicker>
        )}

        <Tooltip
          direction="bottom"
          tabIndex={0}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <p>{getTooltipText(task)}</p>
        </Tooltip>
      </div>
    </TableCell>
  );
};

export default InlineDateEditingCell;
